class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.defaultLoadingDuration = 1000;
    this.loadingHideTimeout = null;
    this.loadingMinVisibleUntil = 0;
    this.loadingStateActive = false;
    this.loadingRequestCount = 0;
    this.loadingLayer = this.querySelector('[data-cart-drawer-loading]');
    this._handleDrawerLoadingStart = (event) => {
      const minDuration = event && event.detail && typeof event.detail.minDuration === 'number'
        ? event.detail.minDuration
        : undefined;
      this.showLoadingOverlay(minDuration);
    };
    this._handleDrawerLoadingStop = () => {
      this.hideLoadingOverlay();
    };
    this.refreshLoadingLayer();
  }

  connectedCallback() {
    document.addEventListener('cart:drawer-loading-start', this._handleDrawerLoadingStart);
    document.addEventListener('cart:drawer-loading-stop', this._handleDrawerLoadingStop);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:drawer-loading-start', this._handleDrawerLoadingStart);
    document.removeEventListener('cart:drawer-loading-stop', this._handleDrawerLoadingStop);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));
    this.refreshLoadingLayer();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  refreshLoadingLayer() {
    this.loadingLayer = this.querySelector('[data-cart-drawer-loading]');
    if (!this.loadingLayer) return;

    if (this.loadingStateActive) {
      this.loadingLayer.classList.add('is-visible');
      this.loadingLayer.setAttribute('aria-hidden', 'false');
    } else {
      this.loadingLayer.classList.remove('is-visible');
      this.loadingLayer.setAttribute('aria-hidden', 'true');
    }
  }

  showLoadingOverlay(minDuration) {
    if (this.loadingRequestCount < 0) this.loadingRequestCount = 0;
    this.loadingRequestCount += 1;
    const layer = this.getLoadingLayer();

    if (this.loadingHideTimeout) {
      clearTimeout(this.loadingHideTimeout);
      this.loadingHideTimeout = null;
    }

    const duration = typeof minDuration === 'number' ? Math.max(minDuration, 0) : this.defaultLoadingDuration;
    const visibleUntil = Date.now() + duration;
    this.loadingMinVisibleUntil = Math.max(this.loadingMinVisibleUntil, visibleUntil);
    this.loadingStateActive = true;

    if (layer) {
      layer.classList.add('is-visible');
      layer.setAttribute('aria-hidden', 'false');
    }
  }

  hideLoadingOverlay() {
    if (this.loadingRequestCount > 0) {
      this.loadingRequestCount -= 1;
    }
    if (this.loadingRequestCount > 0) {
      return;
    }

    const remaining = this.loadingMinVisibleUntil - Date.now();
    if (remaining > 0) {
      if (this.loadingHideTimeout) clearTimeout(this.loadingHideTimeout);
      this.loadingHideTimeout = setTimeout(() => this.forceHideLoadingOverlay(), remaining);
      return;
    }

    this.forceHideLoadingOverlay();
  }

  forceHideLoadingOverlay() {
    this.loadingHideTimeout = null;
    this.loadingMinVisibleUntil = 0;
    this.loadingStateActive = false;
    const layer = this.getLoadingLayer();
    if (!layer) return;

    layer.classList.remove('is-visible');
    layer.setAttribute('aria-hidden', 'true');
  }

  getLoadingLayer() {
    if (!this.loadingLayer || !this.loadingLayer.isConnected) {
      this.loadingLayer = this.querySelector('[data-cart-drawer-loading]');
    }
    return this.loadingLayer;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
